import * as Sentry from '@sentry/react';
import { isAxiosError } from 'axios';
import { isAppError } from '@/services/app-error';
import { showToast } from '@/shared/ui/Toast/showToast';
import { SessionDurationMinutes } from '../enums/enums';

export const noop = () => {
  // do nothing
};

interface ApiErrorPayload {
  message?: string;
}

interface ErrorWithRequest {
  request?: unknown;
}

const hasRequest = (error: unknown): error is ErrorWithRequest =>
  typeof error === 'object' && error !== null && 'request' in error;

export const getApiErrorMessage = (error: unknown, fallback: string) => {
  if (isAppError(error)) {
    return error.message || fallback;
  }

  if (isAxiosError<ApiErrorPayload>(error)) {
    return error.response?.data?.message ?? fallback;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

export const handleCaughtApiError = (error: unknown, fallback = 'An unexpected error occurred. Please try again.') => {
  const message = getApiErrorMessage(error, fallback);
  const sourceError = isAppError(error) && error.originalError ? error.originalError : error;

  showToast.error(message);
  Sentry.captureException(sourceError);

  if (isAxiosError(sourceError) && sourceError.response) {
    console.error('Error:', sourceError);
  } else if (hasRequest(sourceError)) {
    // The request was made but no response was received.
    console.error('Error:', sourceError.request);
  } else if (sourceError instanceof Error) {
    // Something happened in setting up the request that triggered an Error.
    console.error('Error:', sourceError.message);
  } else {
    console.error('Error:', sourceError);
  }
};

export const getHoursLeftForTheDay = (): number => {
  // WHY: Used to determine if a session can still be scheduled today based on remaining hours.
  const currentDate = new Date();
  // 1: Set the time to midnight for the current date
  const startOfDay = new Date(currentDate);
  startOfDay.setHours(0, 0, 0, 0);
  // 2: Get the current time
  const currentTime = currentDate.getTime();
  // 3: Calculate the difference between the end of the day and the current time
  const endOfDay = new Date(startOfDay);
  endOfDay.setDate(endOfDay.getDate() + 1); // Move to the next day
  const timeDifference = endOfDay.getTime() - currentTime;
  // 4: Convert the time difference from milliseconds to hours
  const hoursRemaining = timeDifference / (60 * 60 * 1000);
  return Math.floor(hoursRemaining);
};

export const displaySessionDuration = (duration: number): string => {
  let result = '';
  switch (duration) {
    case SessionDurationMinutes.Sixty:
      result = '1 hour';
      break;
    case SessionDurationMinutes.Ninety:
      result = '1 h 30 min';
      break;
    case SessionDurationMinutes.HundredFive:
      result = '1 h 45 min';
      break;
    case SessionDurationMinutes.HundredTwenty:
      result = '2 hours';
      break;

    default:
      result = duration + ' mins';
      break;
  }
  return result;
};
